import { z } from 'zod';

import { ApiResponseError, assertOk, parseJson } from './response';
import {
  loadInventoryCache,
  mergeNetworkInventorySnapshot,
  recordInventoryAdjustment,
  recordInventoryReorderPoint,
} from '@/lib/offline/inventoryCache';
import { emitInventoryConflictTelemetry } from '@/lib/offline/telemetry';
import type { OfflineQueuedResult } from '@/lib/offline/types';
import type {
  CreateInventoryPayload,
  InventoryCollectionResponse,
  InventoryItemRecord,
  InventoryMutationResponse,
  UpdateInventoryPayload,
} from '@/types/inventory';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';
type ApiFetchFn = typeof import('./requests')['apiFetch'];

const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === 'function') {
    try {
      const mockedModule = jestGlobal.requireMock('@/lib/api/requests') as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === 'function') {
        return mockedModule.apiFetch;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import('./requests');
  return module.apiFetch;
};

type RequestOptions<T> = {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal;
  tenantSlug?: string;
  schema?: z.ZodType<T>;
};

const InventoryItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    unit: z.string().nullable().optional(),
    onHand: z.number().optional(),
    reorderPoint: z.number().optional(),
    menuItemId: z.string().nullable().optional(),
    menuItemName: z.string().nullable().optional(),
    menuItemIsActive: z.boolean().nullable().optional(),
    menuItemAvailability: z.unknown().nullable().optional(),
    createdAt: z.number().optional(),
    updatedAt: z.number().optional(),
  })
  .passthrough();

const InventoryCollectionSchema = z
  .object({
    items: z.array(InventoryItemSchema),
  })
  .passthrough();

const InventoryMutationSchema = z
  .object({
    item: InventoryItemSchema,
  })
  .passthrough();

const OfflineQueuedSchema = z
  .object({
    queued: z.literal(true),
    queueId: z.string(),
    meta: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();

const LowStockResponseSchema = z
  .object({
    items: z.array(InventoryItemSchema),
  })
  .passthrough();

const isOfflineQueuedResult = (value: unknown): value is OfflineQueuedResult => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as OfflineQueuedResult;
  return candidate.queued === true && typeof candidate.queueId === 'string';
};

const buildPendingFallbackItem = (id: string): InventoryItemRecord => ({
  id,
  name: 'Pending inventory update',
  unit: null,
  onHand: 0,
  reorderPoint: 0,
  menuItemId: null,
  menuItemName: null,
  menuItemIsActive: null,
  menuItemAvailability: null,
  createdAt: Date.now(),
  updatedAt: Date.now(),
});

async function requestJson<T>(
  path: string,
  { method = 'GET', body, signal, tenantSlug, schema }: RequestOptions<T> = {},
): Promise<T> {
  const apiFetch = await resolveApiFetch();
  const targetSchema = schema ?? (z.unknown() as z.ZodType<T>);
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (tenantSlug) {
    headers['X-Tenant'] = tenantSlug;
  }

  const shouldStringify = body !== undefined && !(body instanceof FormData);
  if (shouldStringify && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await apiFetch(path, {
    method,
    body: shouldStringify ? JSON.stringify(body) : (body as BodyInit | undefined),
    signal,
    headers,
  });

  if (response.status === 204) {
    await assertOk(response);
    const parsed = targetSchema.safeParse(undefined);
    if (!parsed.success) {
      throw new ApiResponseError('Expected JSON response', response.status);
    }
    return parsed.data;
  }

  await assertOk(response);
  return parseJson(response, targetSchema);
}

export async function fetchInventory(
  tenantSlug: string,
  signal?: AbortSignal,
): Promise<InventoryCollectionResponse> {
  try {
    const response = await requestJson('/inventory', {
      signal,
      tenantSlug,
      schema: InventoryCollectionSchema,
    });
    const networkItems = Array.isArray(response.items) ? response.items : [];
    const { items, conflicts } = mergeNetworkInventorySnapshot(tenantSlug, networkItems);
    emitInventoryConflictTelemetry(tenantSlug, conflicts);
    return {
      items,
      cacheStatus: 'network',
      cachedAt: Date.now(),
    };
  } catch (error) {
    const snapshot = loadInventoryCache(tenantSlug);
    if (snapshot) {
      return {
        items: snapshot.items.map((item) => ({ ...item })),
        cacheStatus: 'cache',
        cachedAt: snapshot.lastSyncedAt ?? Date.now(),
      };
    }
    throw error;
  }
}

export async function getLowStock(
  params?: string,
  signal?: AbortSignal,
): Promise<InventoryCollectionResponse> {
  const path = '/inventory/low-stock' + (params || '');
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(path, {
    signal,
    headers: {
      Accept: 'application/json',
    },
  });

  if (response.status === 404) {
    return { items: [] };
  }

  await assertOk(response);
  const payload = await parseJson(response, LowStockResponseSchema);
  return { items: payload.items };
}

export async function fetchLowStock(signal?: AbortSignal): Promise<InventoryCollectionResponse> {
  return getLowStock(undefined, signal);
}

export async function createInventoryItem(
  tenantSlug: string,
  payload: CreateInventoryPayload,
): Promise<InventoryMutationResponse> {
  return requestJson('/inventory', {
    method: 'POST',
    body: payload,
    tenantSlug,
    schema: InventoryMutationSchema,
  });
}

export async function updateInventoryItem(
  tenantSlug: string,
  id: string,
  payload: UpdateInventoryPayload,
): Promise<InventoryMutationResponse> {
  const response = await requestJson(`/inventory/${id}`, {
    method: 'PUT',
    body: payload,
    tenantSlug,
    schema: z.union([InventoryMutationSchema, OfflineQueuedSchema]),
  });

  if (!isOfflineQueuedResult(response)) {
    return response;
  }

  if (typeof payload.reorderPoint === 'number') {
    const updated = recordInventoryReorderPoint(tenantSlug, id, payload.reorderPoint);
    return { item: updated ?? buildPendingFallbackItem(id) };
  }

  return { item: buildPendingFallbackItem(id) };
}

export async function adjustInventoryItem(
  tenantSlug: string,
  id: string,
  delta: number,
): Promise<InventoryMutationResponse> {
  const response = await requestJson(`/inventory/${id}/adjust`, {
    method: 'POST',
    body: { delta },
    tenantSlug,
    schema: z.union([InventoryMutationSchema, OfflineQueuedSchema]),
  });

  if (!isOfflineQueuedResult(response)) {
    return response;
  }

  const updated = recordInventoryAdjustment(tenantSlug, id, delta);
  return { item: updated ?? buildPendingFallbackItem(id) };
}

export async function deleteInventoryItem(tenantSlug: string, id: string): Promise<void> {
  await requestJson(`/inventory/${id}`, { method: 'DELETE', tenantSlug });
}

export type {
  InventoryItemRecord,
  InventoryCollectionResponse,
  InventoryMutationResponse,
  CreateInventoryPayload,
  UpdateInventoryPayload,
} from '@/types/inventory';
